import Tools from '../Tools/Tools';
import Validation from '../Validation/Validation';
/**
 * Vytváří a ovládá falešný select box.
 * Třída nabízí možnost vygenerovat fake selectbox NEBO
 * ho lze předat ve formě HTML elementu a třída se postará o jeho ovládání.
 */
class Select {
	/**
	 * Constuctor.
	 * @param {boolean} multiSelect Pokud "true", tak lze mít v selectu více vybraných hodnot.
	 */
	constructor(multiSelect = false) {
		this.multiSelect = multiSelect;
		this.hiddenInputsContainer = null;
		// Index aktuálně vybrané option. Používá se pro ovládání klávesnicí.
		this.selectOptionIndex = 0;
		this._outsideClick = this._listenOnOutsideClick.bind(this);
		this._selectListener = this._selectKeyboardControl.bind(this);
	}

	/**
	 * Odstraní inputy a nastaví select na první option.
	 */
	resetSelect() {
		this.removeHiddenInputs();

		if (this.selectOptionPairs.length) {
			this.setSelectOption(this.selectOptionPairs[0][0]);
		}

		if (this.originalButtonText) {
			this.selectButton.innerText = this.originalButtonText;
		}
	}

	/**
	 * Akceptuje ref na select box v DOMu, který bude ovládat.
	 * @param {Element} select Element, který bude ovládán.
	 * @param {Function} onSelect Funkce, která bude vyvolána při výběru option.
	 */
	initSelectWithElement(select, onSelect) {
		if (!select || !onSelect) {
			return;
		}

		this.select = select;
		this.onSelect = onSelect;

		this.selectList = select.querySelector('.Select-options');

		this.selectListOptions = Array.from(this.selectList.getElementsByClassName('Select-option'));

		// Select button.
		this.selectButton = this.select.querySelector('.Select-button');
		this.originalButtonText = this.selectButton.textContent;
		this.selectButton.addEventListener('click', this.toggleSelect.bind(this));

		this._setInputName();
		this._generateOptionPairs();

		this.setSelectOption(this.selected, Boolean(this.selectButton.innerText.length));
	}

	/**
	 * Je potřeba z HTML získat selectOptionPairs [[value, text]...]
	 */
	_generateOptionPairs() {
		this.selectOptionPairs = [];

		this.selectListOptions.forEach((option) => {
			this.selectOptionPairs.push([option.getAttribute('value'), option.textContent]);

			// Získání aktuálně vybrané hodnoty.
			if (option.getAttribute('data-preselected') === 'true') {
				this.selectButton.textContent = option.textContent;
				this.selected = option.getAttribute('value');

				if (!this.multiSelect) {
					this.serviceHiddenInputs(this.selected);
				}
			}

			// Funkce reagující na výběr optionu.
			option.addEventListener('click', (e) => this._changeSelectIntervalByEvent(e));
		});

		if (!this.selected) {
			this._setDefaultSelected();

			if (!this.multiSelect) {
				this.serviceHiddenInputs(this.selected);
			}
		}
	}

	/**
	 * První option je ve výchozím stavu vybraná.
	 */
	_setDefaultSelected() {
		if (this.selectListOptions.length) {
			this.selected = this.selectListOptions[0].getAttribute('value');
		} else {
			this.selected = null;
		}
	}

	/**
	 * Jména inputů, které se generují do formuláře.
	 */
	_setInputName() {
		this.inputName = this.select.getAttribute('data-name');
	}

	/**
	 * Vygeneruje titulky jednotlivých sekcí. (Čtvercově, obdelníkové apod.)
	 * Mohou být selecty, které mají option rozřazené do group.
	 */
	_generateGroupTitles() {
		Array.from(this.selectList.querySelectorAll('.Select-group')).forEach((group) => {
			if (group.children.length) {
				if (!group.children[0].classList.contains('Select-group-title')) {
					const groupTitle = Tools.createElement('h4', {
						class: 'Select-group-title',
						innerText: group.getAttribute('data-label'),
					});

					group.insertBefore(groupTitle, group.children[0]);
				} else if (group.children.length === 1) {
					group.removeChild(group.children[0]);
				}
			}
		});
	}

	/**
	 * Vytvoří select na základě daných parametrů.
	 * @param {[]} selectIntervals Pole s options select boxu
	 * @param {Function} onSelect Funkce, která bude vyvolána při výběru option.
	 * @param {string} selected Výchozí hodnota selectu.
	 * @param {string} WrapperClass Třída pro wrapper.
	 * @param {string} ButtonClass Třída pro tlačítko select boxu.
	 * @param {string} OptionsClass Třída pro list options.
	 * @param {string} OptionClass Třída pro option.
	 */
	initSelectWithParams(
		selectIntervals,
		onSelect,
		selected = '',
		WrapperClass = '',
		ButtonClass = '',
		OptionsClass = '',
		OptionClass = '',
	) {
		this.selectOptionPairs = selectIntervals;
		this.onSelect = onSelect;
		this._outsideClick = this._listenOnOutsideClick.bind(this);
		// aktuálně vybraný interval (today apod.)
		this.selected = selected;
		this.optionsClass = OptionsClass;
		this._generateSelect(WrapperClass, ButtonClass, OptionClass);
	}

	/**
	 * Vrátí select option např. 'today'.
	 * @return {string} Vrací selected.
	 */
	getSelectedOption() {
		return this.selected;
	}

	/**
	 * Vrátí fake select element.
	 * @return {Element} Vrací select.
	 */
	getElement() {
		return this.select;
	}

	/**
	 * Vrací option element na základě předané value.
	 * @param {string} value
	 * @return {Element}
	 */
	getSelectedOptionElem(value) {
		return this.select.querySelector(`.Select-option[value="${value}"]`);
	}

	/**
	 * Obnoví options selectu podle zadaného parametru.
	 * @param {[]} options
	 */
	setNewOptions(options) {
		// Efektivní odstranění všech potomků. (rychlejší než innerHTML ='')
		while (this.selectList.firstChild) {
			this.selectList.removeChild(this.selectList.firstChild);
		}

		// Znovu vytvoření options.
		this.selectOptionPairs = options;

		if (this.selectOptionPairs.length) {
			this.generateOptions();

			// Nastavení vybrané hodnoty selectu. Pokud není předchozí hodnota v nových options,
			// tak bude nastavena na první hodnotu v nových options (selectOptionPairs[0][0]).
			if (this.selectOptionPairs.map((select) => String(select[0])).includes(this.getSelectedOption())) {
				this.setSelectOption(this.getSelectedOption());
			} else {
				this.setSelectOption(this.selectOptionPairs[0][0]);
			}
		}
	}

	/**
	 * Otevře nebo zavře položky selectu. Pokud se select box otevírá,
	 * tak naslouchám na ovládání klávesnicí.
	 * @param {Event} e Event vyvolaný při otevírání/zavírání inputu.
	 */
	toggleSelect(e) {
		if (e) {
			if (!this.selectList.children.length || !this.selectListOptions.length) {
				return;
			}

			e.preventDefault();
		}

		// OPTION_HEIGHT je nutné kvůli posunutí boxu na vybraný prvek a posun klávesnicí.
		if (!this.OPTION_HEIGHT) {
			this.OPTION_HEIGHT = this.selectList.querySelector('.Select-option').offsetHeight;
		}

		this.selectList.classList.toggle('is-open');

		if (this.selectButton.classList.toggle('is-open')) {
			document.addEventListener('keydown', this._selectListener);
			document.addEventListener('click', this._outsideClick);
			// Posun selectBoxu na vybranou položku.
			this.selectList.scrollTop = this.selectOptionIndex * this.OPTION_HEIGHT;
		} else {
			document.removeEventListener('keydown', this._selectListener);
			document.removeEventListener('click', this._outsideClick);
			this._resetSearch();
		}
	}

	/**
	 * Vrátím vše ohledne hledání do původního nastavení.
	 */
	_resetSearch() {
		if (this.searchInput) {
			this.select.removeChild(this.searchInput);

			// Options obnovím až po dokončení animace -> vypadá to líp.
			setTimeout(() => {
				this.selectListOptions.forEach((option) => option.classList.remove('is-invisible'));

				this.searchInput = null;
			}, 400);
		}
	}

	/**
	 * Nastavý překlad hodnoty 'today' => 'Dnes' do tlačítka selectu. A označí aktuálně vybraný.
	 * @param {string} selected Vybraná hodnota 'today', 'yesterday' apod.
	 * @param {boolean} keepButtonText Pokud chci nechat výchozí jméno tlačítka, tak false.
	 */
	setSelectOption(selected, keepButtonText = false) {
		if (!keepButtonText) {
			this.selectOptionPairs.forEach((interval) => {
				// eslint-disable-next-line typescript-eslint/no-unnecessary-type-conversion
				if (String(interval[0]) === String(selected)) {
					this.selectButton.innerText = interval[1];
				}
			});
		}

		this.selectListOptions.forEach((option, index) => {
			// eslint-disable-next-line typescript-eslint/no-unnecessary-type-conversion
			if (String(selected) === String(option.getAttribute('value'))) {
				option.classList.add('is-selected');
				this.selectOptionIndex = index;
			} else {
				option.classList.remove('is-selected');
			}
		});

		this.selected = selected;
		this._generateGroupTitles();
	}

	/**
	 * Schová error selectu, pokud existuje.
	 */
	hideSelectError() {
		if (this.select.nextElementSibling && this.select.nextElementSibling.classList.contains('Error')) {
			this.select.nextElementSibling.classList.add('is-invisible');
		}
	}

	/**
	 * Zobrazí error selectu, pokud existuje.
	 */
	showSelectError() {
		if (this.select.nextElementSibling && this.select.nextElementSibling.classList.contains('Error')) {
			this.select.nextElementSibling.classList.remove('is-invisible');
		}
	}

	/**
	 * Zavolá funkci předanou do konstruktoru při výběru option selectu.
	 * @param {Event} e
	 */
	_changeSelectIntervalByEvent(e) {
		const selected = e.target.getAttribute('value');

		Validation.hideInputError(this.select);
		this.onSelect(selected, this);
	}

	/**
	 * Zavolá funkci předanou do konstruktoru při výběru option selectu.
	 * @param {string} selected
	 */
	_changeSelectIntervalByString(selected) {
		Validation.hideInputError(this.select);
		this.onSelect(selected, this);
	}

	/**
	 * Pokud je selectbox otevřen a uživatel klikne mimo, tak bude zavřen.
	 */
	_listenOnOutsideClick(e) {
		if (
			!e.target.classList.contains('Select-option') &&
			e.target !== this.selectButton &&
			!e.target.classList.contains('Select-search')
		) {
			this.toggleSelect();
		}
	}

	/**
	 * Přesun na předcházející položku v selectu.
	 * Přeskakuje options, které jsou invisibe (uživatel filtruje).
	 */
	_selectMoveUp() {
		let index = this.selectOptionIndex;

		while (index - 1 >= 0) {
			if (!this.selectListOptions[index - 1].classList.contains('is-invisible')) {
				this.selectListOptions[this.selectOptionIndex].classList.remove('is-selected');

				this.selectOptionIndex = index - 1;

				this.selectListOptions[this.selectOptionIndex].classList.add('is-selected');

				return;
			} else {
				index--;
			}
		}
	}

	/**
	 * Přesun na následující položku v selectu.
	 * Přeskakuje options, které jsou invisibe (uživatel filtruje).
	 */
	_selectMoveDown() {
		let index = this.selectOptionIndex;

		while (index + 1 < this.selectListOptions.length) {
			if (!this.selectListOptions[index + 1].classList.contains('is-invisible')) {
				this.selectListOptions[this.selectOptionIndex].classList.remove('is-selected');

				this.selectOptionIndex = index + 1;

				this.selectListOptions[this.selectOptionIndex].classList.add('is-selected');

				return;
			} else {
				index++;
			}
		}
	}

	/**
	 * Uživatel vybral pomocí enteru konkrétní položku selectu,
	 * takže změna bude aplikována.
	 */
	_selectEnter() {
		for (let i = 0; i < this.selectListOptions.length; i++) {
			if (
				this.selectListOptions[i].classList.contains('is-selected') &&
				!this.selectListOptions[i].classList.contains('is-invisible')
			) {
				this._changeSelectIntervalByString(this.selectListOptions[i].getAttribute('value'));

				break;
			}
		}
	}

	/**
	 * Filtruje options podle hledaného textu.
	 * @param {string} char
	 */
	_searchInOptions(char) {
		if (char.match(/^[a-z0-9]$/i)) {
			if (!this.searchInput) {
				this.searchInput = Tools.createElement('input', {
					class: 'Select-search',
				});

				this.select.insertBefore(this.searchInput, this.selectList);
				this.searchInput.focus();

				this.searchInput.addEventListener('input', () => {
					this._filterOptions();
				});
			}
		}
	}

	/**
	 * Filtruje options na základě vstupu uživatele.
	 */
	_filterOptions() {
		// Kurzor (červené zvýraznění pro posun klávesnicí / enter) se posune na první nalezený prvek.
		let firstSeached = true;

		this.selectListOptions.forEach((option, index) => {
			// Rozměry zón používají v rozměrech '×' místo 'x',
			// takže pro lepší hledání tyto znaky nahrazuji.
			const xDimensions = this.searchInput.value.replace('x', '×');

			if (
				option.textContent.match(new RegExp(this.searchInput.value, 'i')) ||
				option.textContent.match(new RegExp(xDimensions, 'i'))
			) {
				option.classList.remove('is-invisible');

				if (firstSeached) {
					this.selectListOptions[this.selectOptionIndex].classList.remove('is-selected');

					this.selectOptionIndex = index;

					this.selectListOptions[this.selectOptionIndex].classList.add('is-selected');

					firstSeached = false;
				}
			} else {
				option.classList.add('is-invisible');
			}
		});
	}

	/**
	 * Funkce naslouchá na stisk šipek nahoru/dolu a enter,
	 * které mohou být využity pro ovládání kalendáře.
	 * @param {Event} e Stisk klávesnice.
	 */
	_selectKeyboardControl(e) {
		switch (e.keyCode) {
			case 13:
				e.preventDefault();
				this._selectEnter();

				break;
			case 38:
				e.preventDefault();
				this._selectMoveUp();
				this.selectList.scrollTop -= this.OPTION_HEIGHT;

				break;
			case 40:
				e.preventDefault();
				this._selectMoveDown();
				this.selectList.scrollTop += this.OPTION_HEIGHT;

				break;
			default:
				this._searchInOptions(e.key);
		}
	}

	/**
	 * Vygeneruje option selectu.
	 */
	generateOptions() {
		this.selectOptionPairs.forEach((interval) => {
			const div = this.createOption(interval[0], interval[1]);

			this.selectList.appendChild(div);
		});

		// array se využívá při ovládání klávesnicí a je uloženo zde JEDNOU,
		// aby se nepřevádělo při každém stisku klávesnice
		this.selectListOptions = Array.from(this.selectList.getElementsByClassName('Select-option'));
	}

	/**
	 * Vygeneruje HTML FAKE select boxu.
	 */
	_generateSelect(WrapperClass, ButtonClass) {
		// Wrapper selectu
		this.select = Tools.createElement('div', {
			class: WrapperClass + ' Select',
		});

		// Button
		this.selectButton = Tools.createElement('div', {
			class: ButtonClass + ' Select-button',
		});

		this.selectButton.addEventListener('click', this.toggleSelect.bind(this));

		// Vygenerování položek selectu
		this.selectList = Tools.createElement('div', {
			class: this.optionsClass + ' Select-options',
		});

		this.generateOptions();

		Tools.appendChilds(this.select, this.selectButton, this.selectList);
		this.setSelectOption(this.selected);
	}

	/**
	 * Tato funkce odstraní option ze selectu.
	 * @param {string} value - Text/číslo, které identifikuje option (value).
	 * @return {[]} Pole obsahující optiony v selectu.
	 */
	removeOption(value) {
		const option = this.select.querySelector(`div[value="${value}"]`);

		if (!option) {
			return;
		}

		const index = this.selectOptionPairs.map((option_) => String(option_[0])).indexOf(value);

		if (~index) {
			this.selectOptionPairs.splice(index, 1);
		}

		option.parentElement.removeChild(option);

		this.selectListOptions = Array.from(this.selectList.getElementsByClassName('Select-option'));

		if (this.selectOptionPairs.length) {
			this.setSelectOption(this.selectOptionPairs[0][0]);
		}

		return this.selectOptionPairs.map((option_) => option_[0]);
	}

	/**
	 * Tato funkce odstraní option ze selectu.
	 * @param {string} text - Text/číslo, které se nastaví jako text v tlačítku selectu.
	 */
	setCustomHeadlineOption(text) {
		this.selectButton.innerText = text;
	}

	/**
	 * Vytvoří jednu option selectu
	 * @param {string} value
	 * @param {string} title
	 * @param {string} group
	 */
	createOption(value, title, group = null) {
		const div = Tools.createElement('div', {
			class: 'Select-option',
			innerText: title,
			value,
		});

		if (this.optionsClass) {
			div.classList.add(this.optionsClass);
		}

		if (group) {
			div.setAttribute('data-group', group);
		}

		div.addEventListener('click', (e) => this._changeSelectIntervalByEvent(e));

		return div;
	}

	/**
	 * Tato funkce vytvoří nový option v selectu.
	 * @param {[]} option - Pole [value, innerHTML], které identifikuje option.
	 * @param {string} group Group do které option zapadá.
	 * @return {[]} Pole obsahující optiony v selectu.
	 */
	setNewOption(option, group) {
		this.selectOptionPairs.push(option);

		if (group) {
			const div = this.createOption(option[0], option[1], group);

			this.select.querySelector(`.Select-group[data-label=${group}]`).appendChild(div);

			this.selectListOptions = Array.from(this.selectList.getElementsByClassName('Select-option'));
		} else {
			this.regenerateOptions();
		}

		this.setSelectOption(this.selectOptionPairs[0][0]);

		return this.selectOptionPairs.map((interval) => interval[0]);
	}

	/**
	 * Tato funkce přeregeneruje optiony v selectu
	 */
	regenerateOptions() {
		const optionsHtml = this.getElement().querySelector('.Select-options');

		optionsHtml.innerHTML = '';
		this.generateOptions();
	}

	/**
	 * Vrací pole s hodnotami inputu.
	 * @return {array}
	 */
	getHiddenInputsValues() {
		if (this.hiddenInputsContainer) {
			const inputs = Array.from(this.hiddenInputsContainer.querySelectorAll('input'));

			return inputs.map((input) => input.getAttribute('value'));
		} else {
			return [];
		}
	}

	/**
	 * Tato funkce obsluhuje vytváření a mazání hidden inputů.
	 * @param {string} name - Hodnota name pro input.
	 * @param {string} value - Hodnota value pro input.
	 * @param {boolean} single - Pokud se jedná o multi pozici, tak se musí poslat false. Jinak se nastaví automaticky na true, což je single select.
	 * @param {boolean} deleteIfExists - Pokud existuje input, tak ho smaže.
	 */
	serviceHiddenInputs(value, name = null, single = true, deleteIfExists = true) {
		this._generateInputsContainer();

		if (!name) {
			name = this.inputName;
		}

		const input = this.hiddenInputsContainer.querySelector(`input[name="${name}"][value="${value}"]`);

		if (input && deleteIfExists) {
			this.hiddenInputsContainer.removeChild(input);
		} else if (!single) {
			this._addHiddenInput(name, value);
		} else {
			if (this.hiddenInputsContainer.children.length > 0) {
				this.hiddenInputsContainer.removeChild(this.hiddenInputsContainer.children[0]);
			}

			this._addHiddenInput(name, value);
		}
	}

	/**
	 * Vytvoří (pokud neexistuje) konteiner kam se generují skryté inputy.
	 */
	_generateInputsContainer() {
		if (!this.hiddenInputsContainer) {
			this.hiddenInputsContainer = Tools.createElement('div', {
				class: 'Select-hidden-inputs',
			});

			this.getElement().appendChild(this.hiddenInputsContainer);
		}
	}

	/**
	 * Tato funkce vytváří hidden input.
	 * @param {string} name - Hodnota name pro input.
	 * @param {string} value - Hodnota value pro input.
	 */
	_addHiddenInput(name, value) {
		const input = Tools.createElement('input', {
			name,
			type: 'hidden',
			value,
		});

		this.hiddenInputsContainer.appendChild(input);
	}

	/**
	 * Odstraní skryté inputy.
	 */
	removeHiddenInputs() {
		if (this.hiddenInputsContainer) {
			this.hiddenInputsContainer.innerHTML = '';
		}
	}
}

export default Select;
